// Website case-study details — drives /website/:slug pages.
//
// ─── How to add or edit a website case study ──────────────────────────
// 1. Make sure the website is also listed in `data.js > websiteProjects`
//    with the same `slug` (that's what the WebsiteFolio cards link to).
// 2. Add a key here matching that slug. Fill in the fields below.
// 3. Drop screenshots into /app/frontend/public/project/ (or use any URL).
//    The `screenshots` array renders each image full-width, stacked,
//    responsive and lazy-loaded. Each entry can be:
//       • a string URL
//       • { src, caption, alt }
// ──────────────────────────────────────────────────────────────────────

const cover = (img) => img;

export const websiteDetails = {
  'the-book-store': {
    slug: 'the-book-store',
    title: 'The Book Store',
    tagline: 'The smart choice for the future — an e-learning marketplace.',
    cat: 'E-Learning', year: '2023',
    role: 'Lead UI Designer & Front-end Developer',
    duration: '6 weeks',
    stack: ['Next.js', 'Tailwind CSS', 'Sanity CMS', 'Vercel'],
    scope: ['UI Design', 'Front-end', 'Headless CMS', 'Performance'],
    liveUrl: '#',
    cover: cover('/website/bookstore-cover.png'),
    overview:
      'Book Store is a responsive web design concept aimed at building a modern, intuitive, and impactful platform for online education. The goal was to create a learning experience that is not only functional but also visually engaging and easy to navigate for all user types — from students and professionals to instructors. As a UX/UI designer, I was responsible for crafting a seamless user journey from discovery to course enrollment while maintaining clarity, structure, and aesthetic appeal.',
    highlights: [
      'Editorial home, category and detail templates with consistent typographic rhythm.',
      '3-step checkout with stored addresses, instant card validation and order tracking.',
      'CMS-driven landing pages so the marketing team can ship campaigns without engineering.',
    ],
    screenshots: [
      { src: '/website/bookstore.png', maxWidth: 200 },
    ],
  },

  'azad': {
    slug: 'azad',
    title: 'AZAD',
    tagline: 'A complete digital home for India\u2019s leading bus body modifiers.',
    cat: 'Manufacturing', year: '2022',
    role: 'UI/UX Designer',
    duration: '2 weeks',
    stack: ['WordPress', 'Custom Theme', 'GSAP', 'jQuery'],
    scope: ['Brand Web Design', 'Custom Development'],
    liveUrl: '#',
    cover: cover('/website/azad-cover.png'),
    overview:
      'Azad is designed to be a leading platform for bus body modification services. The website will showcase the expertise and offerings of Azad, providing detailed information on various bus modification services, customizations, and projects. The goal is to attract potential clients by highlighting the quality and scope of the modifications available. Building the Azad website requires a combination of thoughtful design, robust development, and comprehensive content management. By focusing on user needs and leveraging modern technologies, Azad aims to become the go-to platform for bus body modification services.',
    highlights: [
      'Modular product catalogue with filterable specifications and downloadable PDFs.',
      'Animated hero showcasing finished vehicles with restrained, premium motion.',
      'Multi-language ready content architecture for future regional expansion.',
    ],
    screenshots: [
      { src: '/website/azad-ui.jpg', maxWidth: 200 },
     

    ],
  },

  'logoipsum': {
    slug: 'logoipsum',
    title: 'LogoIpsum',
    tagline: 'A starter-kit website template for software development studios.',
    cat: 'Template', year: '2023',
    role: 'Designer & Front-end Author',
    duration: '4 weeks',
    stack: ['React', 'Tailwind CSS', 'Framer Motion'],
    scope: ['Template Design', 'Front-end', 'Documentation'],
    liveUrl: '#',
    cover: cover('https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=1800&q=85'),
    overview:
      'A polished starter template aimed at agencies and software studios who need to ship a credible presence in days, not months. Built around a small, well-named token system that\u2019s easy to rebrand.',
    highlights: [
      '10 pre-built sections \u2014 hero, services, work, pricing, testimonials, blog.',
      'Single-file theme override that lets new owners restyle the entire site in minutes.',
      'Lighthouse-tuned: 98+ across performance, accessibility and best practices.',
    ],
    screenshots: [
      'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=1800&q=85',
      'https://images.unsplash.com/photo-1559028012-481c04fa702d?w=1800&q=85',
      'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=1800&q=85',
    ],
  },

  'gadsec-solution': {
    slug: 'gadsec-solution',
    title: 'Gadsec Solution',
    tagline: 'CCTV cameras and security appliances \u2014 a confident B2B presence.',
    cat: 'Security', year: '2021',
    role: 'UI/UX Designer',
    duration: '7 weeks',
    stack: ['HTML5', 'css', 'PHP', 'MySQL'],
    scope: ['Brand Web Design', 'Custom Development', 'Lead Capture'],
    liveUrl: '#',
    cover: cover('/website/gadsec-cover.png'),
    overview:
      'Gadsec is envisioned as a premier online platform dedicated to showcasing and promoting CCTV cameras and security appliances. The website will highlight the range of products, advanced technologies, and robust security solutions offered by Gadsec. The goal is to attract potential customers and partners by presenting Gadsec as a leader in the security industry. Building the Gadsec website requires a combination of thoughtful design, robust development, and comprehensive content management. By focusing on user needs and leveraging modern technologies, Gadsec aims to become the leading platform for CCTV cameras and security appliances.',
    highlights: [
      'Product family pages organised by use case, with quick technical spec downloads.',
      'Dealer & distributor enquiry forms with built-in regional routing.',
      'Lightweight, fast on patchy on-site connections \u2014 their reps demo this on phones.',
    ],
    screenshots: [
       { src: '/website/gadsec.png', maxWidth: 200 },
    ],
  },

  'hughes-chem': {
    slug: 'hughes-chem',
    title: 'Hughes Chem Ltd',
    tagline: 'Bird management, repositioned as a humane science.',
    cat: 'Industrial', year: '2022',
    role: 'UI/UX Designer',
    duration: '5 weeks',
    stack: ['WordPress', 'Custom Theme', 'ACF'],
    scope: ['Brand Web Design', 'Content Strategy', 'CMS'],
    liveUrl: '#',
    cover: cover('/website/huge-cover.png'),
    overview:
      'Hughes Chem Ltd is designed to be the leading platform for showcasing and promoting bird management products aimed at enhancing safety. The website will highlight a range of products, innovative solutions, and the technology used in creating effective bird management systems. The goal is to attract potential customers and partners by presenting Hughes Chem Ltd as an industry leader in bird management solutions. Building the Hughes Chem Ltd website requires a combination of thoughtful design, robust development, and comprehensive content management. By focusing on user needs and leveraging modern technologies, Hughes Chem Ltd aims to become the leading platform for bird management products for safety purposes.',
    highlights: [
      'Editorial product pages with research notes, certifications and case data.',
      'Geo-aware partner finder for facility managers across multiple regions.',
      'Custom photography direction that replaced stock imagery on every key page.',
    ],
    screenshots: [
       { src: '/website/huge.png', maxWidth: 200 },
    ],
  },

  'knox-artistie': {
    slug: 'knox-artistie',
    title: 'Knox Artistie',
    tagline: 'A musician\u2019s home for the album \u201cBottle Khol\u201d.',
    cat: 'Music', year: '2024',
    role: 'Designer & Developer',
    duration: '3 weeks',
    stack: ['Next.js', 'Tailwind CSS', 'GSAP', 'Vercel'],
    scope: ['Brand Web Design', 'Motion', 'Front-end'],
    liveUrl: '#',
    cover: cover('/website/knox-cover.png'),
    overview:
      'Knox Artiste is designed to be the official platform for promoting the versatile singer Knox and his newly released album "Bottle Khol." The website will highlight the albums tracks, music videos, tour dates, and exclusive content. The goal is to engage fans, provide them with the latest updates, and offer a seamless experience for discovering and enjoying Knoxs music. Building the Knox Artiste website requires a combination of engaging design, robust development, and comprehensive content management. By focusing on fan engagement and leveraging modern technologies, Knox Artiste aims to become the go-to platform for fans to connect with Knox and enjoy his music.',
    highlights: [
      'Audio-led hero with a one-tap play state that doubles as the brand mark.',
      'Streaming, tour-dates and merch handled inline \u2014 no detours, no second sites.',
      'Custom grainy texture pass on every image to match the album art language.',
    ],
    screenshots: [
      { src: '/website/knox.png', maxWidth: 200 },
    ],
  },

  'wellness-india': {
    slug: 'wellness-india',
    title: 'Wellness India',
    tagline: 'Pan-India platform for wellness and health services.',
    cat: 'Healthcare', year: '2023',
    role: 'Lead UI/UX Designer',
    duration: '10 weeks',
    stack: ['React', 'Node.js', 'MongoDB', 'Tailwind CSS'],
    scope: ['UX Strategy', 'UI Design', 'Booking Flow', 'CMS'],
    liveUrl: '#',
    cover: cover('/website/wellness-cover.png'),
    overview:
      'Wellness India aims to be the premier online platform dedicated to providing comprehensive wellness and health-related services. The website will offer a range of services, resources, and tools to promote a healthy lifestyle and well-being. The goal is to attract individuals seeking to improve their health and connect them with professionals and resources that can help them achieve their wellness goals. Building the Wellness India website requires a combination of thoughtful design, robust development, and comprehensive content management. By focusing on user needs and leveraging modern technologies, Wellness India aims to become the leading platform for wellness and health-related services.',
    highlights: [
      'Practitioner profiles with verified credentials, reviews and same-day booking.',
      '2-minute booking flow with smart defaults based on location and prior visits.',
      'A separate, calmer admin panel so practitioners can manage availability quickly.',
    ],
    screenshots: [
      { src: '/website/wellness.png', maxWidth: 200 },
    ],
  },

  'dubai-tickets': {
    slug: 'dubai-tickets',
    title: 'Dubai Tickets',
    tagline: 'A travel concierge for the modern Dubai itinerary.',
    cat: 'Travel', year: '2024',
    role: 'UI/UX Designer & Front-end',
    duration: '8 weeks',
    stack: ['Next.js', 'Tailwind CSS', 'Stripe', 'Contentful'],
    scope: ['UX Strategy', 'UI Design', 'Booking & Payments'],
    liveUrl: '#',
    cover: cover('/website/dubai-cover.png'),
    overview:
      'Dubai Tickets is an online platform designed to offer a seamless and user-friendly experience for booking tickets across various tours and attractions in Dubai. The platform aims to enhance user satisfaction by providing a streamlined, efficient booking process with clear navigation and instant ticket confirmations. The goal is to deliver an exceptional user experience tailored to tourists looking to explore Dubais diverse attractions. The website saw a significant improvement in user engagement and conversion rates after its launch. Users reported a more streamlined booking process, and mobile bookings increased by 40% in the first three months. The real-time availability feature reduced booking conflicts, improving overall customer satisfaction.',
    highlights: [
      'Editorial experience pages with hero video, itinerary timeline, and inclusions.',
      'One-page checkout with Apple Pay, Google Pay and saved travellers.',
      'Smart upsells that nudge add-ons without ever feeling like a hard sell.',
    ],
    screenshots: [
      { src: '/website/dubai.jpg', maxWidth: 200 },
    ],
  },
};

export const websiteDetailBySlug = (slug) => websiteDetails[slug];
